import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import { AccessParamList } from '../../../typings/Params';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { useAuth } from '../../../context/Auth';
import GenericUserDropdown from '../../../components/Dropdowns/GenericUserDropdown';
import ActingBodiesUnitsDropdown from '../../dispatch/Units/ActingBodiesUnitsDropdown';
import PermissionsLevelsDropdown from '../../../components/Filters/Access/PermissionLevelsDropdown';

interface Props {
    filters: AccessParamList['PermissionLevels'];
    setFilters: React.Dispatch<React.SetStateAction<AccessParamList['PermissionLevels']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function Filter({ filters, setFilters, setTextFilter, textFilter }: Props) {
    const { user: authenticatedUser } = useAuth();

    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitIds, setActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [userId, setUserId] = useState<number>(filters.userId ?? 0);
    const [permissions, setPermission] = useState<(keyof UserPermissions)[]>(filters.permissions ?? []);

    const navigation = useNavigation<NavigationProp<AccessParamList, 'PermissionLevels'>>();

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyUnitIds([]);
        setActingBodyId(0);
        setUserId(0);
        setPermission([]);
    };

    const handleFilter = () => {
        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
            userId: userId !== 0 ? userId : undefined,
            permissions: permissions.length > 0 ? permissions : undefined
        });

        navigation.setParams({
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
            textFilter: textFilter !== '' ? textFilter : undefined,
            userId: userId !== 0 ? userId : undefined,
            page: 1,
            permissions: permissions.length > 0 ? permissions : undefined
        });
    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        } else if (authenticatedUser.isAdmin || authenticatedUser.permissions.access_edit_permission_level_settings_all_acting_bodies) {
            return actingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    const customSetActingBodyId: React.Dispatch<React.SetStateAction<number>> = (value) => {
        setActingBodyId(value);
        setActingBodyUnitIds([]);
        setUserId(0);
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.access_edit_permission_level_settings_all_acting_bodies) &&
                <ActingBodiesDropdown
                    value={actingBodyId}
                    setValue={customSetActingBodyId}
                    zIndex={5} />
            }
            {(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.access_edit_permission_level_settings_all_acting_bodies
                || authenticatedUser?.permissions.access_edit_permission_level_settings_acting_body_unit) &&
                <ActingBodiesUnitsDropdown
                    actingBodyId={getActingBodyId()}
                    value={actingBodyUnitIds}
                    setValue={setActingBodyUnitIds}
                    zIndex={4} />
            }
            <PermissionsLevelsDropdown
                setValue={setPermission}
                value={permissions}
                zIndex={3}
            />
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />
            <GenericUserDropdown
                value={userId}
                setValue={setUserId}
                actingBodyId={getActingBodyId()}
                zIndex={2}
                label='user' />
        </Filters>
    );
}

export default Filter;
